import type { Component, VNode } from 'vue'
import type { ToastProps } from '.'
import { computed, ref } from 'vue'

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY = 100 // Changed from 1000000 to 100ms

export type StringOrVNode =
  | string
  | VNode
  | (() => VNode)

type ToasterToast = ToastProps & {
  id: string
  title?: string
  description?: StringOrVNode
  action?: Component
  /** Stays until Refresh / explicit dismiss; survives other toasts. */
  persistent?: boolean
}

const actionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_VALUE
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
    type: ActionType['ADD_TOAST']
    toast: ToasterToast
  }
  | {
    type: ActionType['UPDATE_TOAST']
    toast: Partial<ToasterToast>
  }
  | {
    type: ActionType['DISMISS_TOAST']
    toastId?: ToasterToast['id']
  }
  | {
    type: ActionType['REMOVE_TOAST']
    toastId?: ToasterToast['id']
  }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

function addToRemoveQueue(toastId: string) {
  if (toastTimeouts.has(toastId)) {
    clearTimeout(toastTimeouts.get(toastId)) // Clear existing timeout
    toastTimeouts.delete(toastId)
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: actionTypes.REMOVE_TOAST,
      toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

const state = ref<State>({
  toasts: [],
})

function dispatch(action: Action) {
  switch (action.type) {
    case actionTypes.ADD_TOAST: {
      const incoming = action.toast
      const persistent = state.value.toasts.filter(t => t.persistent)
      if (incoming.persistent) {
        // One persistent toast (e.g. UI update); drop older persistent ones.
        const others = state.value.toasts.filter(t => !t.persistent).slice(0, TOAST_LIMIT)
        state.value.toasts = [incoming, ...others]
      }
      else {
        const nonPersistent = [incoming, ...state.value.toasts.filter(t => !t.persistent)].slice(
          0,
          TOAST_LIMIT,
        )
        state.value.toasts = [...persistent, ...nonPersistent]
      }
      break
    }

    case actionTypes.UPDATE_TOAST:
      state.value.toasts = state.value.toasts.map(t =>
        t.id === action.toast.id ? { ...t, ...action.toast } : t,
      )
      break

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action

      const targets = toastId
        ? state.value.toasts.filter(t => t.id === toastId)
        : state.value.toasts

      targets.forEach((t) => {
        // Persistent update toast only closes via its Refresh button (explicit dismiss by id is allowed).
        if (t.persistent && !toastId) return
        addToRemoveQueue(t.id)
      })

      state.value.toasts = state.value.toasts.map(t => {
        if (toastId && t.id !== toastId) return t
        if (!toastId && t.persistent) return t
        return { ...t, open: false }
      })
      break
    }

    case actionTypes.REMOVE_TOAST:
      if (action.toastId === undefined)
        state.value.toasts = state.value.toasts.filter(t => t.persistent)
      else
        state.value.toasts = state.value.toasts.filter(t => t.id !== action.toastId)

      break
  }
}

function useToast() {
  return {
    toasts: computed(() => state.value.toasts),
    toast,
    dismiss: (toastId?: string) => dispatch({ type: actionTypes.DISMISS_TOAST, toastId }),
  }
}

type Toast = Omit<ToasterToast, 'id'>

function toast(props: Toast) {
  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: actionTypes.UPDATE_TOAST,
      toast: { ...props, id },
    })

  const dismiss = () => {
    // Allow programmatic dismiss even for persistent toasts (e.g. versions now match).
    ;(props as { __forceDismiss?: boolean }).__forceDismiss = true
    dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id })
  }

  // Set default duration if not provided; persistent toasts never auto-close.
  const duration = props.persistent ? 0 : (props.duration ?? 5000)

  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open: boolean) => {
        const force = Boolean((props as { __forceDismiss?: boolean }).__forceDismiss)
        // Ignore swipe/escape close for persistent update toast — Refresh or force-dismiss only.
        if (!open && props.persistent && !force) {
          dispatch({
            type: actionTypes.UPDATE_TOAST,
            toast: { id, open: true },
          })
          return
        }
        if (!open) dismiss()
        props.onOpenChange?.(open)
      },
    },
  })

  if (duration > 0) {
    setTimeout(() => {
      dismiss()
    }, duration)
  }

  return {
    id,
    dismiss,
    update,
  }
}

export { toast, useToast }
